import React from 'react';
import { Box, Container, Typography, Card, CardContent, Button, Grid, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import HomeBackground from "../../assets/images/HomeBackground.png";

// Info Cards Data
const infoCardsData = [
  {
    id: 1,
    icon: <PsychologyIcon />,
    title: "Experience",
    description: "Learning with Skill Serve is just the start of your journey. Our courses provide hands-on skills and real-world knowledge to make you job-ready. Gain practical experience, work on industry-relevant projects, and confidently launch your career in high-demand industries."
  },
  {
    id: 2,
    icon: <SchoolIcon />,
    title: "Education",
    description: "Education is the foundation for growth, shaping minds and unlocking potential. It goes beyond knowledge, fostering critical thinking, creativity, and adaptability. The right education empowers individuals to build skills, embrace challenges, and contribute meaningfully to the world."
  },
  {
    id: 3,
    icon: <WorkspacePremiumIcon />,
    title: "Certification",
    description: "Earn an NSDC-recognized certificate that validates your journey of learning and growth. Whether building skills or pursuing your passion, this certification sets you apart and unlocks endless possibilities!"
  }
];

// Styled Components
const SectionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '600px',
  backgroundImage: `url(${HomeBackground})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(8, 0),
  marginTop: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    minHeight: '500px',
    padding: theme.spacing(6, 0),
    backgroundAttachment: 'scroll',
    marginTop: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 'auto',
    padding: theme.spacing(4, 0),
    marginTop: theme.spacing(2),
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, rgba(14, 43, 81, 0.92) 0%, rgba(26, 61, 107, 0.88) 100%)',
  zIndex: 1,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  width: '100%',
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  color: '#ffffff',
  '&:hover': {
    transform: 'translateY(-8px)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(247, 196, 45, 0.5)',
    boxShadow: '0 12px 40px rgba(247, 196, 45, 0.2)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #f7c42d 0%, #e5b327 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 4px 15px rgba(247, 196, 45, 0.3)',
  '& .MuiSvgIcon-root': {
    fontSize: '1.8rem',
    color: '#0e2b51',
  },
  [theme.breakpoints.down('sm')]: {
    width: '50px',
    height: '50px',
    '& .MuiSvgIcon-root': {
      fontSize: '1.5rem',
    },
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#f7c42d',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  fontFamily: 'Hind Mysuru, sans-serif',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.3rem',
  },
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  lineHeight: 1.7,
  color: 'rgba(255, 255, 255, 0.9)',
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  textAlign: 'justify',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    lineHeight: 1.6,
  },
}));

const CTACard = styled(Card)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  border: '3px solid transparent',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    borderColor: '#f7c42d',
    transform: 'scale(1.02)',
    boxShadow: '0 16px 50px rgba(247, 196, 45, 0.3)',
  },
}));

const CTATitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.8rem',
  fontWeight: 800,
  color: '#0e2b51',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  fontFamily: 'Hind Mysuru, sans-serif',
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
  },
}));

const CTADescription = styled(Typography)(({ theme }) => ({
  fontSize: '1.05rem',
  lineHeight: 1.8,
  color: '#4a4a4a',
  marginBottom: theme.spacing(3),
  fontFamily: 'Hind Mysuru, sans-serif',
  fontWeight: 500,
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.95rem',
    lineHeight: 1.6,
  },
}));

const CTAButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f7c42d 0%, #e5b327 100%)',
  color: '#0e2b51',
  fontSize: '1.1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 4),
  borderRadius: '30px',
  textTransform: 'none',
  fontFamily: 'Hind Mysuru, sans-serif',
  boxShadow: '0 4px 15px rgba(247, 196, 45, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #e5b327 0%, #f7c42d 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(247, 196, 45, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(1.2, 3),
  },
}));

const InfoSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <SectionContainer>
      <Overlay />
      <ContentWrapper>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {/* Info Cards */}
            {infoCardsData.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.id}>
                <InfoCard>
                  <CardContent sx={{ padding: { xs: 3, md: 3.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <IconWrapper>
                      {card.icon}
                    </IconWrapper>
                    <CardTitle>{card.title}</CardTitle>
                    <CardDescription>{card.description}</CardDescription>
                  </CardContent>
                </InfoCard>
              </Grid>
            ))}

            {/* CTA Card */}
            <Grid item xs={12} sm={12} md={3}>
              <CTACard>
                <CardContent sx={{ padding: { xs: 3, md: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CTATitle>
                    Learn Online at Your Own Pace
                  </CTATitle>
                  <CTADescription>
                    Learn online at your own pace, with the flexibility to study whenever and wherever suits you. Master new skills on your terms, without the pressure of a traditional classroom.
                  </CTADescription>
                  <CTAButton
                    onClick={() => navigate("/allCourses/all")}
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                  >
                    Get Started
                  </CTAButton>
                </CardContent>
              </CTACard>
            </Grid>
          </Grid>
        </Container>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default InfoSection;